import {
  CircularProgress,
  Dialog,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  TextareaAutosize,
} from "@mui/material";
import noImage from "assets/images/no-image.png";
import Input from "components/common/Input";
import Select from "components/common/Select";
import api from "config/api";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { setCartBtnLoading, setSelected } from "store/slices/main.slice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CrustType, SizeType } from "utils/types";
import AddIcon from "@mui/icons-material/Add";
import Button from "components/common/Button";
import RemoveIcon from "@mui/icons-material/Remove";
import ModificationPopUp from "./ModificationPopUp";
import VariantSelection from "components/common/VariantSelection";
import { toast } from "react-toastify";
import { addUpdateToCart } from "store/action/main";

type Props = {};

const initialConfig = {
  qty: 1,
  size: 0,
  crust: 0,
  note: "",
  id: "",
  pizzaName: "",
  spiceLevel: "",
  modifications: undefined,
  variant: [],
};

// const spiceData = ["Hot", "Extra Hot", "Mild", "Med"];

const ProductPopUp = (props: Props) => {
  const dispatch = useAppDispatch();

  const [selectedProduct, setSelectedProduct] = useState<any>(undefined);
  const [variants, setVariants] = useState<any>([]);
  const [sizes, setSizes] = useState<SizeType[]>([]);
  const [crusts, setCrusts] = useState<CrustType[]>([]);
  const [showModification, setshowModification] = useState<any>(undefined);
  const [selectedVariantItem, setSelectedVariantItem] = useState({});
  const [config, setConfig] = useState(initialConfig);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentPizzaIndex, setCurrentPizzaIndex] = useState(0);
  const [pizzasInDeal, setPizzasInDeal] = useState<any[]>([]);
  const [spiceData, setSpiceData] = useState<any[]>([]);
  const [checkCartConfigData, setCheckCartConfigData] = useState<number[]>([]);

  const product = useAppSelector(({ main }) => main.selectedProduct);
  const { settings, deliveryType, cartBtnLoading, taxes, pizzaList } =
    useAppSelector(({ main }) => main);

  const allHavePizzaName = useMemo(
    () => pizzasInDeal.every((item) => item.pizzaName.length > 0),
    [pizzasInDeal]
  );

  const variantStep = useMemo(() => {
    if (!variants?.[currentStep]) {
      return { data: [], mandatoryLevel: 0, qtyLevel: 0 };
    }

    return variants[currentStep];
  }, [variants, currentStep]);

  const allVariants = useMemo(
    () =>
      variants.reduce((obj: any, { data = [] }: any) => {
        data.forEach((element: any) => {
          obj[element.id] = element;
          element?.subLevelData?.forEach((subElement: any) => {
            obj[subElement.id] = subElement;
          });
        });
        return obj;
      }, {}),
    [variants]
  );

  const steps = useMemo(() => {
    if (!selectedProduct || selectedProduct?.isPizza) return [];
    const keys = variants.map((_: any, i: number) => i + 1);
    return [...keys, keys.length + 1];
  }, [selectedProduct, variants]);

  const totalModifications = useMemo(
    () => Object.values(config?.modifications || {}).flat().length,
    [config]
  );

  useEffect(() => {
    const id = product?.productId;
    if (id && product.isPizza) {
      api
        .get("/api/Modifier/PizzaSubLevels?productId=" + id)
        .then(({ data }) => {
          setSpiceData(data);
          // setConfig((e) => ({ ...e, spiceLevel: data?.[0].modifierName }));
        })
        .catch((e) => {});
    }
  }, [product]);

  useEffect(() => {
    return () => {
      dispatch(setCartBtnLoading(false));
    };
  }, [dispatch]);
  useEffect(() => {
    if (!product?.data) return;

    if (
      (product.data.isDeal && product.data.isPizza) ||
      product.data.pizzasInDeal > 0
    ) {
      const cartonfig =
        pizzasInDeal?.length > 0
          ? {
              ...pizzasInDeal?.[currentPizzaIndex],
            }
          : {
              ...product.data?.selectedDealPizzas?.[currentPizzaIndex],
            };

      const indexFind = checkCartConfigData.findIndex(
        (e) => e === currentPizzaIndex
      );

      if (indexFind === -1) {
        setConfig({ ...cartonfig?.config });
        setCheckCartConfigData((e) => [...e, currentPizzaIndex]);
      }
    }

    if (product.data.config.variant) {
      setSelectedVariantItem(product.data.config.variant?.[0] || {});
    }
  }, [checkCartConfigData, currentPizzaIndex, pizzasInDeal, product?.data]);

  useEffect(() => {
    if (product?.data) {
      setSelectedProduct(product.data);

      if (
        (product.data.isDeal && product.data.isPizza) ||
        product.data.pizzasInDeal > 0
      ) {
        setPizzasInDeal([...product.data?.selectedDealPizzas]);
      } else {
        setCheckCartConfigData([0]);
        setConfig(product.data.config);
      }
    } else {
      setSelectedProduct(product);
      if (
        (product?.isDeal && product?.isPizza) ||
        (product?.pizzasInDeal && product?.pizzasInDeal > 0)
      ) {
        const initialPizzas = Array(product.pizzasInDeal).fill({
          ...initialConfig,
        });
        setPizzasInDeal(initialPizzas);
        setConfig(initialPizzas[0]);
      }
    }
  }, [product]);

  useEffect(() => {
    if (selectedProduct) {
      if (selectedProduct.isPizza) {
        api
          .get(
            `/api/${
              selectedProduct.isCreateYourOwn
                ? "PizzaToppingPrice"
                : "PizzaSize/ByProductId"
            }?productId=${selectedProduct.productId}`
          )
          .then(({ data }) => {
            if (!product?.isEdit) {
              setConfig((e) => ({ ...e, size: data[0].sizeId }));
            }
            setSizes(data);
          })
          .catch(() => {});
      } else {
        api
          .get(
            `/api/Variation/VariantById?productId=${selectedProduct.productId}`
          )
          .then(({ data }) => setVariants(data))
          .catch(() => {});
      }
    }
  }, [selectedProduct, product]);

  useEffect(() => {
    if (selectedProduct && selectedProduct.isPizza && config.size) {
      api
        .get(
          `/api/Modifier/Crusts?sizeId=${config.size}&crustId=0&productId=${selectedProduct.productId}`
        )
        .then(({ data }) => {
          if (!product?.isEdit) {
            setConfig((e) => ({ ...e, crust: data[0].modifierId }));
          }
          setCrusts(data);
        })
        .catch(toast.error);
    }
  }, [config.size, selectedProduct, product]);

  const addItemToCart = () => {
    // if (!!selectedProduct.isPizza) {
    //   if (!config.spiceLevel) {
    //     toast.error("Select spice level is required !!");
    //     return;
    //   }
    // }

    const updatedPizzasInDeal = [...pizzasInDeal];
    updatedPizzasInDeal[currentPizzaIndex] = config;
    toast.dismiss();
    const [tax1 = { rate: 0 }, tax2 = { rate: 0 }, tax3 = { rate: 0 }] =
      taxes ?? [];
    const pizza = pizzaList.find((i) => i.productId === config.id);
    const size = sizes.find((i) => i.sizeId === config.size);
    const crust = crusts.find((i) => i.modifierId === config.crust);

    if (!selectedProduct) return;

    const isDeal = selectedProduct.isDeal;
    const isPizza = selectedProduct.isPizza;
    const isCreateYourOwn = selectedProduct.isCreateYourOwn;
    const isPizzasInDeal = selectedProduct.pizzasInDeal > 0;

    const basePrice =
      settings.ApplyTakeOutDeliveryPrice !== 0
        ? deliveryType !== "pickup"
          ? selectedProduct?.deliveryprice || selectedProduct.price
          : selectedProduct?.takeoutprice || selectedProduct.price
        : !!selectedProduct.isDeal || !!selectedProduct.isCreateYourOwn
        ? 0
        : selectedProduct.price;

    const finalProduct: any = {
      data: {},
      id: selectedProduct.productId,
      productId: selectedProduct.productId,
      productCode: selectedProduct.productCode,
      fullDescription: selectedProduct.productName,
      quantity: config.qty,
      price: basePrice,
      variationsPrice: 0,
      totalUnitPrice: basePrice,
      subTotal: basePrice * config.qty,
      itemTotal: 0,
      taxId1: selectedProduct.taxId1,
      taxId2: selectedProduct.taxId2,
      taxId3: selectedProduct.taxId3,
      note: config.note,
      totalTax1: 0,
      totalTax2: 0,
      totalTax3: 0,
      status: "new",
      modifiers: [],
      isDeal,
      isPizza,
      isCreateYourOwn,
      taxRate1: tax1?.rate ?? 0,
      taxRate2: tax2?.rate ?? 0,
      taxRate3: tax3?.rate ?? 0,
    };

    if (product?.data) {
      finalProduct.data = {
        ...product.data,
        config,
        isEdit: true,
        index: product.index,
      };
    } else {
      finalProduct.data = { ...product, config };
    }

    // if (isPizza || isDeal || isCreateYourOwn) {
    if (isPizza || isDeal || isCreateYourOwn) {
      if (!size || !crust) {
        toast.error("Size and Crust are required");
        return;
      }
      finalProduct.pizzaCrust = {
        crustId: crust.modifierId,
        crustName: crust.modifierName,
        crustPrice: crust.modifierCost,
      };

      if (crust.modifierCost) {
        finalProduct.itemTotal += crust.modifierCost;
      }

      finalProduct.pizzaSize = {
        pizzasizeId: size.sizeId,
        size: size.sizeName,
      };
    }
    if (isDeal || isPizza) {
      finalProduct.spiceLevel = {
        spiceId: 1,
        spicename: config.spiceLevel,
      };
    }

    if (isDeal || isPizzasInDeal) {
      finalProduct.data["selectedDealPizzas"] = updatedPizzasInDeal.map(
        (pizzaConfig) => {
          // old code
          // const pizzaSize = sizes.find((s) => s.sizeId === pizzaConfig?.size);
          // const crustDetails = crusts.find(
          //   (c) => c.modifierId === pizzaConfig.crust,
          // );

          const pizzaSize = sizes.find((s) => s.sizeId === pizzaConfig.size);
          const foundSpice = spiceData.find(
            (el) => el.modifierName === pizzaConfig?.spiceLevel
          );
          // const pizzaSize = sizes.find((s) =>
          //   pizzaConfig?.size
          //     ? s.sizeId === pizzaConfig?.size
          //     : pizzaConfig?.pizzaSize?.pizzasizeId,
          // );
          // const foundSpice = spiceData.find((el) =>
          //   typeof pizzaConfig?.spiceLevel === "object"
          //     ? el.modifierId === pizzaConfig.spiceLevel?.spiceId
          //     : el.modifierName === pizzaConfig?.spiceLevel,
          // );

          const subTotal = +(getSizePrice(pizzaSize) || 0);
          const dealPizza = {
            id: pizzaConfig?.id,
            crust: pizzaConfig.crust,
            config: pizzaConfig, // old config
            productId: selectedProduct.productId,
            productCode: selectedProduct.productCode,
            fullDescription: selectedProduct.productName,
            quantity: 1,
            price: subTotal,
            variationsPrice: 0,
            totalUnitPrice: selectedProduct.price,
            subTotal,
            itemTotal: subTotal,
            taxId1: selectedProduct.taxId1,
            taxId2: selectedProduct.taxId2,
            taxId3: selectedProduct.taxId3,
            note: pizzaConfig.note,
            totalTax1: 0,
            totalTax2: 0,
            totalTax3: 0,
            status: "new",
            modifiers: pizzaConfig.modifications
              ? Object.values(pizzaConfig.modifications)
                  .flat()
                  .map((item: any) => ({
                    defaultTopping: item.defaultTopping,
                    newDefaultTopping: item.newDefaultTopping,
                    modifierId: item.pizzaModifierId,
                    description: item.pizzaModifierName,
                    quantity: 1,
                    unitPrice: item.pizzaModifierPrice,
                    subTotal: item.pizzaModifierPrice,
                    totalValue: item.pizzaModifierPrice,
                    note: "",
                    isFree:
                      item.defaultTopping || item.newDefaultTopping ? 0 : 0,
                    pizzaRegion: 1,
                  }))
              : [],
            isDeal: 1,
            isPizza: 1,
            isCreateYourOwn: 0,
            taxRate1: tax1?.rate ?? 0,
            taxRate2: tax2?.rate ?? 0,
            taxRate3: tax3?.rate ?? 0,
            spiceLevel: {
              spiceId: foundSpice?.modifierId || 1,
              spicename: foundSpice?.modifierName || config?.spiceLevel,
            },
            pizzaSize: {
              pizzasizeId: pizzaSize?.sizeId,
              size: pizzaSize?.sizeName || config.size,
            },
            pizzaName: pizzaConfig.pizzaName ?? selectedProduct?.productName,
          };

          // Calculate taxes
          if (selectedProduct.taxId1 === 1 && tax1?.rate) {
            const taxValue = +(dealPizza.subTotal * (tax1?.rate / 100)).toFixed(
              5
            );
            dealPizza.totalTax1 = taxValue;
            dealPizza.itemTotal += taxValue;
          }
          if (selectedProduct.taxId2 === 2 && tax2?.rate) {
            const taxValue = +(dealPizza.subTotal * (tax2?.rate / 100)).toFixed(
              5
            );
            dealPizza.totalTax2 = taxValue;
            dealPizza.itemTotal += taxValue;
          }
          if (selectedProduct.taxId3 === 3 && tax3?.rate) {
            const taxValue = +(dealPizza.subTotal * (tax3?.rate / 100)).toFixed(
              5
            );
            dealPizza.totalTax3 = taxValue;
            dealPizza.totalUnitPrice = dealPizza.itemTotal;
            dealPizza.itemTotal += taxValue;
          }

          if (!!config?.modifications) {
            const [toppingTotal]: any = Object.values(
              config?.modifications
            ).filter((fil: any) => fil.length > 0);

            finalProduct.subTotal += toppingTotal?.reduce(
              (sum: number, item: any, index: number) =>
                index >= selectedProduct.toppingsEachPizza
                  ? sum + item.pizzaModifierPrice
                  : sum + 0,
              0
            );
          }

          finalProduct.subTotal += subTotal * config.qty;
          return dealPizza;
        }
      );

      finalProduct.itemTotal = finalProduct.subTotal;
      finalProduct.totalTax1 = finalProduct.data.selectedDealPizzas.reduce(
        (sum: number, pizza: any) => sum + pizza.totalTax1,
        0
      );
      finalProduct.totalTax2 = finalProduct.data.selectedDealPizzas.reduce(
        (sum: number, pizza: any) => sum + pizza.totalTax2,
        0
      );
      finalProduct.totalTax3 = finalProduct.data.selectedDealPizzas.reduce(
        (sum: number, pizza: any) => sum + pizza.totalTax3,
        0
      );

      finalProduct.itemTotal +=
        finalProduct.totalTax1 +
        finalProduct.totalTax2 +
        finalProduct.totalTax3;
    } else {
      const [baseAMt] = Object.entries(size || {}).reduce(
        ([tk, tv], [key, value]: any) => {
          if (!key.includes("toppingPrice") || !value) return [tk, tv];
          const v = +key.replace("toppingPrice", "");
          if (tk < v) return [v, value];
          return [tk, tv];
        },
        [0, 0]
      );

      if (config?.modifications) {
        finalProduct.modifiers = Object.values(config?.modifications || {})
          .flat()
          .map((item: any, index: number) => ({
            defaultTopping: item.defaultTopping,
            newDefaultTopping: item.newDefaultTopping,
            modifierId: item.pizzaModifierId,
            description: item.pizzaModifierName,
            quantity: 1,
            unitPrice: item.pizzaModifierPrice,
            subTotal: item.pizzaModifierPrice,
            totalValue: item.pizzaModifierPrice,
            note: "",
            isFree: index < baseAMt ? 1 : 0,
            pizzaRegion: 1,
          }));

        const toppingPrice =
          Object.values(config?.modifications || {})
            .flat()
            .filter((i: any) => !i.defaultTopping && !i.newDefaultTopping)
            .slice(baseAMt)
            .reduce((t: number, i: any) => (t += i.pizzaModifierPrice), 0) || 0;

        finalProduct.itemTotal += toppingPrice;
      } else {
        finalProduct.modifiers = selectedProduct.defaultPizzaToppings.map(
          (item: any, index: number) => ({
            defaultTopping: 1,
            newDefaultTopping: 0,
            modifierId: item.modifierId,
            description: item.modifierName,
            quantity: 1,
            unitPrice: 0,
            subTotal: 0,
            totalValue: 0,
            note: "",
            isFree: 1,
            pizzaRegion: 1,
          })
        );
      }
      if ((config?.variant || []).length > 0) {
        const tempVariants: any[] = [];
        config?.variant.forEach((item) => {
          Object.entries(item).forEach(([key, value]: any) => {
            if (allVariants?.[key]) {
              finalProduct.variationsPrice += allVariants?.[key].price;
              tempVariants.push({ ...allVariants[key], isSubLevel: 0 });
            }
            Array.from(value).forEach((subItem: any) => {
              if (allVariants?.[subItem]) {
                finalProduct.variationsPrice += allVariants[subItem].price;
                tempVariants.push({ ...allVariants[subItem], isSubLevel: 1 });
              }
            });
          });
        });

        finalProduct.selectedItemsLevel = tempVariants;
        finalProduct.totalUnitPrice += finalProduct.variationsPrice;
      }
      if (
        selectedProduct &&
        !!selectedProduct.isPizza &&
        !!selectedProduct.isDeal &&
        !selectedProduct.isCreateYourOwn
      ) {
        if (!pizza) {
          toast.error("Pizza item is required");
          return;
        }
      }
      if (selectedProduct.isCreateYourOwn || selectedProduct.isPizza) {
        const newPrice = +(getSizePrice(size) || "0");
        if (newPrice) {
          finalProduct.pizzaSize.price = newPrice;
          finalProduct.price = newPrice;
        }
      }
      finalProduct.itemTotal +=
        finalProduct.price + finalProduct.variationsPrice; // confirm
      finalProduct.subTotal = finalProduct.itemTotal * config.qty;
      finalProduct.totalUnitPrice = finalProduct.itemTotal;
      if (selectedProduct.taxId1 === 1 && tax1?.rate) {
        const total = +((finalProduct.subTotal * tax1?.rate) / 100).toFixed(5);
        finalProduct.totalTax1 = total;
        finalProduct.taxRate1 = tax1?.rate ?? 0;
        finalProduct.itemTotal += total;
      } else {
        finalProduct.totalTax1 = 0;
        finalProduct.taxRate1 = 0;
      }
      if (selectedProduct.taxId2 === 2 && tax2?.rate) {
        const total = +((finalProduct.subTotal * tax2?.rate) / 100).toFixed(5);
        finalProduct.totalTax2 = total;
        finalProduct.taxRate2 = tax2?.rate ?? 0;
        finalProduct.itemTotal += total;
      } else {
        finalProduct.totalTax2 = 0;
        finalProduct.taxRate2 = 0;
      }
      if (selectedProduct.taxId3 === 3 && tax3?.rate) {
        const total = +((finalProduct.subTotal * tax3.rate) / 100).toFixed(5);
        finalProduct.totalTax3 = total;
        finalProduct.taxRate3 = tax3?.rate ?? 0;
        finalProduct.itemTotal += total;
      } else {
        finalProduct.totalTax3 = 0;
        finalProduct.taxRate3 = 0;
      }
    }
    // }

    // dispatch(addToCart(finalProduct));
    dispatch(addUpdateToCart(finalProduct, onClose));
  };

  const onClose = () => {
    setConfig(initialConfig);
    setCurrentStep(0);
    setCurrentPizzaIndex(0);
    setPizzasInDeal([]);
    dispatch(setSelected(undefined));
    setCheckCartConfigData([]);
  };

  const moveToNextStep = (value?: any) => {
    const newConfig: any = structuredClone(config);
    newConfig.variant[currentStep] = value || selectedVariantItem;
    setConfig(newConfig);
    if (product?.data) {
      setSelectedVariantItem(
        product.data.config?.variant?.[currentStep + 1] || {}
      );
    } else {
      const nextVariants: any = {};
      const nextVariantData = variants?.[currentStep + 1];
      if (nextVariantData) {
        if (
          nextVariantData?.mandatoryLevel === 0 &&
          nextVariantData?.data?.length === 1
        ) {
          nextVariants[nextVariantData?.data[0].id] = new Set();
        }
        setSelectedVariantItem(nextVariants);
      } else {
        setSelectedVariantItem({});
      }
    }
    setCurrentStep((e) => e + 1);
  };

  const getSizePrice = useCallback(
    (item: any) => {
      if (selectedProduct?.isCreateYourOwn) {
        if (!totalModifications) return item?.toppingPrice1 || "";
        if (item?.[`toppingPrice${totalModifications}`])
          return item[`toppingPrice${totalModifications}`];
        const [, basePrice] = Object.entries(item || {}).reduce(
          ([tk, tv], [key, value]: any) =>
            (key.includes("toppingPrice") && value) > tv
              ? [+key.replace("toppingPrice", ""), value]
              : [tk, tv],
          [0, 0]
        );
        return basePrice;
      }

      if (selectedProduct?.isPizza) return item?.sizePriceX1;
    },
    [selectedProduct, totalModifications]
  );

  const goToNextPizza = () => {
    const nextIndex = Math.min(currentPizzaIndex + 1, pizzasInDeal.length - 1);
    // if (!!selectedProduct.isPizza) {
    // if (!config.spiceLevel) {
    //   toast.error("Select spice level is required !!");
    //   return;
    // }
    // }
    const updatedPizzas = [...pizzasInDeal];
    updatedPizzas[currentPizzaIndex] = { ...config };
    setPizzasInDeal([...updatedPizzas]);
    setCurrentPizzaIndex(nextIndex);
    const nextPizza = { ...updatedPizzas[nextIndex] };
    nextPizza.size = config.size;
    setConfig(nextPizza);
  };

  const goToPreviousPizza = () => {
    const updatedPizzas = [...pizzasInDeal];
    updatedPizzas[currentPizzaIndex] = { ...config };
    setPizzasInDeal([...updatedPizzas]);
    const prevIndex = Math.max(currentPizzaIndex - 1, 0);
    setCurrentPizzaIndex(prevIndex);
    setConfig(updatedPizzas[prevIndex]);
  };

  return (
    <Dialog
      onClose={onClose}
      open={!!selectedProduct}
      maxWidth="lg"
      className="select-none"
    >
      {selectedProduct && (
        <div className="p-4 md:min-w-[600px] sm:min-w-[300px] flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            {selectedProduct?.imageUrl && (
              <div className="flex justify-center items-center sm:w-1/3 w-full">
                <img
                  src={selectedProduct?.imageUrl || noImage}
                  className="w-full max-h-[150px] h-auto object-cover rounded-lg border border-gray-300"
                  alt="product"
                />
              </div>
            )}
            <div className="sm:w-2/3 w-full text-center sm:text-left">
              <h4 className="font-bold text-[18px] sm:text-[20px]">
                {selectedProduct.productName}
              </h4>
              <p className="text-[14px] sm:text-[15px] capitalize">
                {selectedProduct.productDescription.toLowerCase()}
              </p>
            </div>
          </div>
          {((selectedProduct &&
            selectedProduct.isDeal === 1 &&
            selectedProduct.isPizza === 1) ||
            selectedProduct.pizzasInDeal > 0) && (
            <div className="flex justify-between items-center mb-4">
              <Button
                onClick={goToPreviousPizza}
                disabled={currentPizzaIndex === 0}
              >
                Previous Pizza
              </Button>
              <span>
                Pizza {currentPizzaIndex + 1} of {pizzasInDeal.length}
              </span>
              <Button
                onClick={goToNextPizza}
                disabled={
                  currentPizzaIndex === pizzasInDeal.length - 1 ||
                  totalModifications < (product?.toppingsEachPizza || 0)
                }
              >
                Next Pizza
              </Button>
            </div>
          )}
          {!!selectedProduct.isPizza &&
            !!selectedProduct.isDeal &&
            !selectedProduct.isCreateYourOwn && (
              <Select
                label="Pizza Item"
                value={config.id}
                onValueChange={(id) => {
                  const newConfig = {
                    ...config,
                    id,
                    pizzaName: pizzaList.find((item) => item.productId === id)
                      .productName,
                  };
                  const updatedPizzas = [...pizzasInDeal];
                  updatedPizzas[currentPizzaIndex] = newConfig;
                  setPizzasInDeal(updatedPizzas);
                  setConfig(newConfig);
                }}
              >
                {pizzaList.map((item) => (
                  <MenuItem value={item.productId}>{item.productName}</MenuItem>
                ))}
              </Select>
            )}
          {!!selectedProduct.isPizza ? (
            <div className="flex max-md:flex-col gap-3">
              <Select
                label="Size"
                value={config.size}
                onValueChange={(size) =>
                  setConfig((v) => ({ ...v, crust: 0, size }))
                }
              >
                {sizes.map((item) => (
                  <MenuItem value={item.sizeId}>
                    {item.sizeName}{" "}
                    {!!getSizePrice(item) ? `[$${getSizePrice(item)}]` : ""}
                  </MenuItem>
                ))}
              </Select>
              <Select
                label="Select Spice"
                value={config.spiceLevel}
                onValueChange={(spiceLevel) =>
                  setConfig((v) => ({ ...v, spiceLevel }))
                }
                clearable
              >
                {spiceData.map((item) => (
                  <MenuItem value={item.modifierName}>
                    {item.modifierName}
                  </MenuItem>
                ))}
              </Select>
              <Select
                label="Crusts"
                value={config.crust}
                onValueChange={(crust) => setConfig((v) => ({ ...v, crust }))}
              >
                {crusts.map((item) => (
                  <MenuItem value={item.modifierId}>
                    {`${item.modifierName} ${
                      item.modifierCost ? `($${item.modifierCost})` : ""
                    }`}
                  </MenuItem>
                ))}
              </Select>
              {/* {selectedProduct.isDeal ? (
                <Select
                  label="Spice Level"
                  value={config.spiceLevel}
                  onValueChange={(spiceLevel) =>
                    setConfig((v) => ({ ...v, spiceLevel }))
                  }
                >
                  {spiceData.map((item) => (
                    <MenuItem value={item}>{item}</MenuItem>
                  ))}
                </Select>
              ) : (
                <Select
                  label="Crusts"
                  value={config.crust}
                  onValueChange={(crust) => setConfig((v) => ({ ...v, crust }))}
                >
                  {crusts.map((item) => (
                    <MenuItem value={item.modifierId}>
                      {item.modifierName} (${item.modifierCost})
                    </MenuItem>
                  ))}
                </Select>
              )} */}
            </div>
          ) : (
            <>
              {steps.length > 1 && (
                <Stepper activeStep={currentStep} alternativeLabel>
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>Step {label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              )}
            </>
          )}
          {!selectedProduct.isPizza && !!variants.length && (
            <VariantSelection
              selected={selectedVariantItem}
              setSelected={setSelectedVariantItem}
              moveToNextStep={moveToNextStep}
              option={variantStep}
            />
          )}
          {!!selectedProduct.isPizza || currentStep === steps.length - 1 ? (
            <>
              <TextareaAutosize
                className="border border-[#0000003b] p-2 rounded hover:border-black focus:!ring-primary"
                placeholder="Note..."
                minRows={5}
                value={config.note}
                onChange={(e) =>
                  setConfig((v) => ({ ...v, note: e.target.value }))
                }
              />
              <div className="flex justify-center gap-2">
                <Button
                  className="!px-2"
                  onClick={() =>
                    config.qty > 1 &&
                    setConfig((v) => ({ ...v, qty: v.qty - 1 }))
                  }
                >
                  <RemoveIcon className="!text-light-text" />
                </Button>
                <Input
                  size="small"
                  className="max-w-[80px]"
                  inputProps={{ style: { textAlign: "center" } }}
                  value={config.qty}
                  onValueChange={(value) => {
                    let qty = +value;
                    if (isNaN(qty) || qty < 1) {
                      qty = 1;
                    }
                    if (qty > 9999) {
                      qty = 9999;
                    }
                    setConfig((v) => ({ ...v, qty }));
                  }}
                />
                <Button
                  className="!px-2"
                  onClick={() =>
                    setConfig((v) => ({
                      ...v,
                      qty: v.qty < 9999 ? v.qty + 1 : v.qty,
                    }))
                  }
                >
                  <AddIcon className="!text-light-text" />
                </Button>
              </div>
              {!!product?.toppingsEachPizza &&
                totalModifications < (product?.toppingsEachPizza || 0) && (
                  <div>
                    <p className="text-red-500">
                      Note: Minimum {product?.toppingsEachPizza} toppings
                      required
                    </p>
                  </div>
                )}
              <div className="flex max-sm:flex-col gap-2 justify-between  mt-3">
                {steps.length > 1 ? (
                  <Button
                    className="min-h-[45px]"
                    onClick={() => {
                      if (selectedProduct.isPizza) {
                        onClose();
                      } else {
                        setSelectedVariantItem(
                          config.variant?.[currentStep - 1] || {}
                        );
                        setCurrentStep((e) => e - 1);
                      }
                    }}
                  >
                    {selectedProduct.isPizza ? "Cancel" : "Previous"}
                  </Button>
                ) : (
                  <div />
                )}

                {!!selectedProduct.isPizza && (
                  <Button
                    className="min-h-[45px]"
                    disabled={
                      !!selectedProduct.isPizza &&
                      !!selectedProduct.isDeal &&
                      !selectedProduct.isCreateYourOwn
                        ? !config.pizzaName
                        : false
                    }
                    onClick={() =>
                      setshowModification({
                        sizeId: config.size,
                        modifications: config.modifications,
                        productId: selectedProduct.productId,
                      })
                    }
                  >
                    Customize [PIZZA]
                  </Button>
                )}

                <Button
                  disabled={
                    !(
                      !!selectedProduct.isCreateYourOwn &&
                      totalModifications >= (product?.toppingsEachPizza || 0) &&
                      currentPizzaIndex === Math.max(0, pizzasInDeal.length - 1)
                    ) &&
                    !(
                      selectedProduct.isDeal &&
                      selectedProduct.isPizza &&
                      currentPizzaIndex === pizzasInDeal.length - 1 &&
                      allHavePizzaName
                    ) &&
                    (!!selectedProduct?.isCreateYourOwn ||
                      !!selectedProduct?.isDeal)
                  }
                  className="min-h-[45px] flex items-center gap-2"
                  onClick={addItemToCart}
                >
                  {cartBtnLoading && (
                    <CircularProgress
                      size={20}
                      className="!text-themeSelColor"
                    />
                  )}
                  {product?.isEdit
                    ? "Update Cart"
                    : (selectedProduct.isDeal && selectedProduct.isPizza) ||
                      selectedProduct.pizzasInDeal > 0
                    ? "Add Deal to Cart"
                    : "Add to Cart"}
                </Button>
              </div>
            </>
          ) : (
            <div className="flex justify-between items-center sticky bottom-0 bg-white">
              <Button
                className="min-h-[45px] m-1"
                onClick={() => {
                  if (currentStep === 0) {
                    onClose();
                  } else {
                    setSelectedVariantItem(
                      config.variant?.[currentStep - 1] || {}
                    );
                    setCurrentStep((e) => e - 1);
                  }
                }}
              >
                {currentStep === 0 ? "Cancel" : "Previous"}
              </Button>
              {(Object.keys(selectedVariantItem).length <
                variantStep.mandatoryLevel ||
                Object.keys(selectedVariantItem).length >
                  variantStep.qtyLevel) && (
                <p className="text-red-500">
                  Select Maximun : {variantStep.qtyLevel} and Minimum :
                  {variantStep.mandatoryLevel}
                </p>
              )}
              <Button
                disabled={
                  Object.keys(selectedVariantItem).length <
                    variantStep.mandatoryLevel ||
                  Object.keys(selectedVariantItem).length > variantStep.qtyLevel
                }
                className="min-h-[45px] mt-1"
                onClick={() => moveToNextStep()}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
      <ModificationPopUp
        open={showModification}
        setOpen={setshowModification}
        onSubmit={(modifications) => {
          setshowModification(undefined);
          setConfig((v) => {
            const toppingTotal = Object.values(modifications).filter(
              (fil: any) => fil.length > 0
            );

            const updatedPizzas = [...pizzasInDeal];
            updatedPizzas[currentPizzaIndex] = !!toppingTotal.length
              ? { ...v, modifications }
              : { ...v };
            setPizzasInDeal(updatedPizzas);

            return {
              ...v,
              modifications: !!toppingTotal.length ? modifications : undefined,
            };
          });
        }}
      />
    </Dialog>
  );
};

export default ProductPopUp;
